import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CornerDownRight, Plus } from "lucide-react";
import { cn } from "@/lib/cn";
import { friendlyError } from "@/lib/errors";
import { useActiveOrg } from "@/features/tenancy/hooks";
import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { Field, Input, Toggle } from "@/components/ui/Form";
import { Modal } from "@/components/ui/Modal";
import { StatusPill } from "@/components/ui/StatusPill";
import { toast } from "@/components/ui/toast";
import { QueryBoundary } from "@/components/ui/QueryBoundary";
import { EmptyState } from "@/components/ui/states";
import {
  createCategory,
  deleteCategory,
  fetchCategories,
  slugify,
  updateCategory,
  type CategoryDraft,
  type CategoryRow,
} from "./taxonomyApi";

/**
 * Categorias en dos niveles: raiz y subcategoria.
 *
 * La base admitiria mas profundidad —`parent_id` se referencia a si misma y
 * solo hay un check que impide ser padre de uno mismo, no ciclos— pero el
 * desplegable del producto dibuja «Padre › Hija», asi que un tercer nivel
 * mostraria «Hija › Nieta» y perderia al abuelo. Limitarlo aqui ademas hace
 * los ciclos imposibles por construccion: una hija nunca puede ser padre.
 */
export function CategoriesPage() {
  const { org } = useActiveOrg();
  const qc = useQueryClient();
  const [editando, setEditando] = useState<CategoryRow | "nueva" | null>(null);
  const [borrando, setBorrando] = useState<CategoryRow | null>(null);
  const [quitando, setQuitando] = useState(false);

  const lista = useQuery({
    queryKey: ["categories", org?.organizationId],
    queryFn: () => fetchCategories(org!.organizationId),
    enabled: Boolean(org),
  });

  const todas = lista.data ?? [];
  const raices = todas.filter((c) => !c.parent_id);
  const hijasDe = (id: string) => todas.filter((c) => c.parent_id === id);

  async function confirmarBorrado() {
    if (!borrando) return;
    setQuitando(true);
    try {
      await deleteCategory(borrando.id);
      toast.ok("Categoría eliminada");
      await qc.invalidateQueries({ queryKey: ["categories", org?.organizationId] });
      await qc.invalidateQueries({ queryKey: ["taxonomy", org?.organizationId] });
      await qc.invalidateQueries({ queryKey: ["products"] });
      setBorrando(null);
    } catch (e) {
      toast.error(friendlyError(e));
    }
    setQuitando(false);
  }

  return (
    <div className="flex flex-col gap-3">
      <Panel
        title="Categorías"
        bleed
        actions={
          <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => setEditando("nueva")}>
            Nueva
          </Button>
        }
      >
        <QueryBoundary
          query={lista}
          isEmpty={(d) => d.length === 0}
          empty={
            <EmptyState
              title="Todavía no hay categorías"
              description="Son el primer filtro de tu tienda: agrupan el catálogo y arman el menú."
            />
          }
        >
          {() => (
            <ul className="divide-y divide-line-2">
              {raices.map((raiz) => (
                <li key={raiz.id}>
                  <Fila categoria={raiz} onEditar={setEditando} onBorrar={setBorrando} />
                  {hijasDe(raiz.id).map((hija) => (
                    <Fila
                      key={hija.id}
                      categoria={hija}
                      anidada
                      onEditar={setEditando}
                      onBorrar={setBorrando}
                    />
                  ))}
                </li>
              ))}
            </ul>
          )}
        </QueryBoundary>
      </Panel>

      <p className="text-xs text-ink-3">
        Las categorías clasifican y filtran. Para filtrar por peso, talla, edad o sabor usa{" "}
        <strong className="font-medium text-ink-2">Atributos</strong>: multiplicar categorías para
        eso deja un menú larguísimo y un producto en un solo sitio.
      </p>

      {editando && (
        <CategoryDialog
          categoria={editando === "nueva" ? null : editando}
          raices={raices}
          organizationId={org!.organizationId}
          onClose={() => setEditando(null)}
          onSaved={async () => {
            await qc.invalidateQueries({ queryKey: ["categories", org?.organizationId] });
            await qc.invalidateQueries({ queryKey: ["taxonomy", org?.organizationId] });
            setEditando(null);
          }}
        />
      )}

      <Modal
        open={Boolean(borrando)}
        onClose={() => setBorrando(null)}
        tone="danger"
        title={`Eliminar ${borrando?.name ?? ""}`}
        description={arrastre(borrando)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setBorrando(null)} disabled={quitando}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={() => void confirmarBorrado()} loading={quitando}>
              Eliminar
            </Button>
          </>
        }
      />
    </div>
  );
}

/**
 * Que se lleva por delante el borrado.
 *
 * La base no se opone a nada: products.category_id y categories.parent_id son
 * ON DELETE SET NULL. Sin este aviso, borrar «Perros» dejaria cinco
 * subcategorias sueltas en la raiz sin que nadie se entere hasta verlo.
 */
function arrastre(c: CategoryRow | null): string {
  if (!c) return "";
  const partes: string[] = [];
  if (c.productos > 0) {
    partes.push(
      `${c.productos} producto${c.productos === 1 ? "" : "s"} se quedará${c.productos === 1 ? "" : "n"} sin categoría`,
    );
  }
  if (c.hijas > 0) {
    partes.push(
      `sus ${c.hijas} subcategoría${c.hijas === 1 ? "" : "s"} pasará${c.hijas === 1 ? "" : "n"} a ser categoría${c.hijas === 1 ? "" : "s"} principal${c.hijas === 1 ? "" : "es"}`,
    );
  }

  const cola = "También desaparece del menú de la tienda. Los productos no se borran.";
  if (partes.length === 0) return `No la usa ningún producto. ${cola}`;
  return `Al eliminarla, ${partes.join(" y ")}. ${cola}`;
}

function Fila({
  categoria,
  anidada = false,
  onEditar,
  onBorrar,
}: {
  categoria: CategoryRow;
  anidada?: boolean;
  onEditar: (c: CategoryRow) => void;
  onBorrar: (c: CategoryRow) => void;
}) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2 px-4 py-2.5",
        anidada && "border-t border-line-2 pl-10",
      )}
    >
      {anidada && <CornerDownRight className="size-3.5 shrink-0 text-ink-3" />}
      <div className="min-w-0 flex-1">
        <span className={cn("text-ui", !anidada && "font-medium")}>{categoria.name}</span>
        <span className="tnum ml-2 text-xs text-ink-3">/{categoria.slug}</span>
      </div>

      <span className="tnum text-xs text-ink-3">
        {categoria.productos} producto{categoria.productos === 1 ? "" : "s"}
      </span>
      {!categoria.active && <StatusPill tone="neutral">Oculta</StatusPill>}

      <Button size="sm" variant="secondary" onClick={() => onEditar(categoria)}>
        Editar
      </Button>
      <Button size="sm" variant="danger" onClick={() => onBorrar(categoria)}>
        Eliminar
      </Button>
    </div>
  );
}

function CategoryDialog({
  categoria,
  raices,
  organizationId,
  onClose,
  onSaved,
}: {
  categoria: CategoryRow | null;
  raices: CategoryRow[];
  organizationId: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [draft, setDraft] = useState<CategoryDraft>({
    name: categoria?.name ?? "",
    slug: categoria?.slug ?? "",
    parent_id: categoria?.parent_id ?? null,
    active: categoria?.active ?? true,
    sort_order: categoria?.sort_order ?? 0,
  });
  const [slugTocado, setSlugTocado] = useState(Boolean(categoria));
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!slugTocado) setDraft((d) => ({ ...d, slug: slugify(d.name) }));
  }, [draft.name, slugTocado]);

  // Una categoria con hijas no puede volverse subcategoria: eso crearia el
  // tercer nivel que esta pantalla no admite.
  const tieneHijas = (categoria?.hijas ?? 0) > 0;
  const posiblesPadres = raices.filter((r) => r.id !== categoria?.id);

  const error = !draft.name.trim() ? "Ponle un nombre." : !draft.slug ? "Falta el enlace." : null;

  async function guardar() {
    if (error) return;
    setGuardando(true);
    try {
      if (categoria) await updateCategory(categoria.id, draft);
      else await createCategory(organizationId, draft);
      toast.ok(categoria ? "Categoría guardada" : "Categoría creada");
      await onSaved();
    } catch (e) {
      toast.error(friendlyError(e));
      setGuardando(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={categoria ? `Editar ${categoria.name}` : "Nueva categoría"}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={() => void guardar()} loading={guardando} disabled={Boolean(error)}>
            {categoria ? "Guardar" : "Crear"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <Field label="Nombre" required>
          <Input
            autoFocus
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </Field>

        <Field
          label="Depende de"
          hint={
            tieneHijas
              ? "Tiene subcategorías, así que solo puede ser una categoría principal."
              : "Déjalo en «Ninguna» para que sea una categoría principal."
          }
        >
          <select
            value={draft.parent_id ?? ""}
            disabled={tieneHijas}
            onChange={(e) => setDraft({ ...draft, parent_id: e.target.value || null })}
            className="w-full rounded-md border border-line bg-surface px-3 py-2 text-ui disabled:opacity-45"
          >
            <option value="">Ninguna · categoría principal</option>
            {posiblesPadres.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Enlace"
          required
          hint="Único en tu tienda: «Alimentos» de perros y de gatos necesitan enlaces distintos."
        >
          <Input
            value={draft.slug}
            onChange={(e) => {
              setSlugTocado(true);
              setDraft({ ...draft, slug: slugify(e.target.value) });
            }}
          />
        </Field>

        <Field label="Orden" hint="Menor aparece antes. Empate: alfabético.">
          <Input
            inputMode="numeric"
            value={String(draft.sort_order)}
            onChange={(e) =>
              setDraft({ ...draft, sort_order: Number(e.target.value.replace(/\D/g, "")) || 0 })
            }
          />
        </Field>

        <Toggle
          label="Visible en la tienda"
          hint="Oculta no aparece en el menú ni en los filtros."
          checked={draft.active}
          onChange={(v) => setDraft({ ...draft, active: v })}
        />
      </div>
    </Modal>
  );
}
